import * as React from 'react';
import { Tooltip } from 'antd';
import humanizeUtil from '@utils/humanizeUtil';
import BaseChart, { useChartInstance } from '@src/components/common/BaseChart';
import { formatGaugeChartData, getThresholds, getTextColor } from '../utils';
import * as Styled from '../styled';

export interface GaugeProps {
  value: number;
  description?: string;
  settings: {
    threshold: {
      t1?: number;
      t2?: number;
    };
    timeout?: number;
    timeout_unit?: string;
  };
}

const unitMapping = {
  seconds: 'sec',
  minutes: 'min',
  hours: 'hr',
};

const Gauge = ({ value, description, settings }: GaugeProps) => {
  const { instance, chartInstanceRefCb } = useChartInstance({});
  const thresholds = getThresholds(settings);
  const scoreColor = getTextColor(value || 0, thresholds.absolute);

  const chartOption = React.useMemo(() => {
    const { humanizedNumber } = humanizeUtil(value, '', '%');
    return formatGaugeChartData(humanizedNumber, thresholds.scaled);
  }, [value, thresholds.scaled]);

  React.useEffect(() => {
    if (instance) {
      instance.setOption(chartOption);
    }
  }, [instance, chartOption]);

  const timeoutText = `Completed within ${settings.timeout ?? 0} ${(settings.timeout_unit && unitMapping[settings.timeout_unit]) ?? ''}`;

  return (
    <Tooltip title={description}>
      <Styled.GaugeContainer>
        <Styled.Title>{timeoutText}</Styled.Title>
        <Styled.GaugeContent>
          <BaseChart
            chartInstanceRefCb={chartInstanceRefCb}
            style={{ width: 48, height: '100%' }}
          />
          <Styled.GaugeValue style={{ color: scoreColor }}>
            {humanizeUtil(value, '', '%').humanizedNumber} %
          </Styled.GaugeValue>
        </Styled.GaugeContent>
      </Styled.GaugeContainer>
    </Tooltip>
  );
};

export default Gauge;
